import { jsPDF } from 'jspdf';
import api from '../../api';
import './YoungApprenticeAttendance.css';

const BLUE = [18, 62, 115];
const TEXT = [24, 50, 79];
const MUTED = [102, 123, 145];
const BORDER = [215, 226, 239];
const LIGHT = [245, 249, 253];
const FOOTER = 'Relatório gerado pelo sistema Intelligent BPM Nash Tecnologia. Site: www.nashtec.com.br';

const text = (value) => String(value ?? '').trim();
const formatTaxId = (value) => {
  const digits = text(value).replace(/\D/g, '').slice(0, 14);
  return digits.length === 14
    ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    : digits;
};
const safeFilename = (value) => text(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9._-]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '') || 'lista-presenca.pdf';

const blobToDataUrl = (blob) => new Promise((resolve) => {
  if (!blob || typeof FileReader === 'undefined') return resolve(null);
  const reader = new FileReader();
  reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
  reader.onerror = () => resolve(null);
  reader.readAsDataURL(blob);
});

const organizationLogoData = async (organization) => {
  const embedded = text(organization?.logoDataUrl);
  if (/^data:image\/(png|jpe?g);base64,/i.test(embedded)) return embedded;
  if (!text(organization?.logoUrl)) return null;
  try {
    const response = await api.get('/company/logo/content', { responseType: 'blob' });
    return await blobToDataUrl(response?.data);
  } catch {
    return null;
  }
};

const drawFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...BORDER);
    doc.line(12, pageHeight - 12, pageWidth - 12, pageHeight - 12);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.2);
    doc.text(FOOTER, pageWidth / 2, pageHeight - 7.5, { align: 'center', maxWidth: pageWidth - 42 });
    doc.text(`Página ${page} de ${totalPages}`, pageWidth - 12, pageHeight - 4.6, { align: 'right' });
  }
};

export async function downloadYoungApprenticeAttendancePdf({ organization, rows = [], courseDay, period }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;
  const logoData = await organizationLogoData(organization);
  const organizationName = text(organization?.tradeName) || text(organization?.name) || 'Organização';
  const taxId = formatTaxId(organization?.taxId);
  const generatedDate = new Intl.DateTimeFormat('pt-BR').format(new Date());
  const normalizedRows = Array.from(new Map(rows.map((row) => [String(row.studentId || row.apprenticeNumber || row.fullName), row])).values())
    .sort((a, b) => text(a.fullName).localeCompare(text(b.fullName), 'pt-BR', { sensitivity: 'base' }));

  const drawHeader = () => {
    doc.setFillColor(...BLUE);
    doc.rect(0, 0, pageWidth, 38, 'F');
    if (logoData) {
      try {
        doc.addImage(logoData, /^data:image\/png/i.test(logoData) ? 'PNG' : 'JPEG', margin, 7, 24, 24);
      } catch {
        // O relatório continua válido mesmo se a imagem institucional estiver indisponível.
      }
    }
    const x = logoData ? margin + 31 : margin;
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('LISTA DE PRESENÇA', x, 12.5);
    doc.setFontSize(9.5);
    doc.text('PROGRAMA JOVEM APRENDIZ', x, 18.2);
    doc.setFontSize(8.5);
    doc.text(organizationName, x, 25.2, { maxWidth: pageWidth - x - margin });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    if (taxId) doc.text(`CNPJ ${taxId}`, x, 30.2);
    doc.text(`Gerado em ${generatedDate}`, pageWidth - margin, 12.5, { align: 'right' });

    doc.setFillColor(...LIGHT);
    doc.setDrawColor(...BORDER);
    doc.roundedRect(margin, 43, pageWidth - margin * 2, 14, 2.3, 2.3, 'FD');
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(`Dia de curso: ${text(courseDay) || 'Todos'}`, margin + 4, 49);
    doc.text(`Período: ${text(period) || 'Todos'}`, margin + 4, 54);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text(`${normalizedRows.length} aprendiz(es)`, pageWidth - margin - 4, 51.5, { align: 'right' });
  };

  const drawTableHeader = (y) => {
    const numberWidth = 12;
    const nameWidth = 82;
    const signWidth = pageWidth - margin * 2 - numberWidth - nameWidth;
    doc.setFillColor(...BLUE);
    doc.rect(margin, y, pageWidth - margin * 2, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('Nº', margin + numberWidth / 2, y + 5.7, { align: 'center' });
    doc.text('Nome do aprendiz', margin + numberWidth + 3, y + 5.7);
    doc.text('Assinatura', margin + numberWidth + nameWidth + 3, y + 5.7);
    return { y: y + 9, numberWidth, nameWidth, signWidth };
  };

  drawHeader();
  let table = drawTableHeader(62);
  let y = table.y;
  const minimumRowHeight = 10.5;
  normalizedRows.forEach((row, index) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const nameLines = doc.splitTextToSize(text(row.fullName), table.nameWidth - 6);
    const rowHeight = Math.max(minimumRowHeight, nameLines.length * 3.25 + 4);
    const nameBaseline = (rowHeight - (nameLines.length - 1) * 3.25) / 2 + 1;
    if (y + rowHeight > pageHeight - 18) {
      doc.addPage();
      drawHeader();
      table = drawTableHeader(62);
      y = table.y;
    }
    if (index % 2 === 1) {
      doc.setFillColor(249, 251, 254);
      doc.rect(margin, y, pageWidth - margin * 2, rowHeight, 'F');
    }
    doc.setDrawColor(...BORDER);
    doc.rect(margin, y, pageWidth - margin * 2, rowHeight);
    const xName = margin + table.numberWidth;
    const xSign = xName + table.nameWidth;
    doc.line(xName, y, xName, y + rowHeight);
    doc.line(xSign, y, xSign, y + rowHeight);
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(String(index + 1), margin + table.numberWidth / 2, y + rowHeight / 2 + 1, { align: 'center' });
    doc.text(nameLines, xName + 3, y + nameBaseline);
    y += rowHeight;
  });

  if (!normalizedRows.length) {
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Nenhum aprendiz encontrado para os filtros selecionados.', margin, y + 9);
  }

  drawFooter(doc);
  const suffix = [courseDay, period].map(text).filter(Boolean).join('-') || generatedDate.replace(/\//g, '-');
  doc.save(safeFilename(`lista-presenca-jovem-aprendiz-${suffix}.pdf`));
}
